import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { User, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ first_name: '', last_name: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [savingPw, setSavingPw] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (user) setProfile({ first_name: user.first_name || '', last_name: user.last_name || '' });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (savingProfile) return;
    setSavingProfile(true);
    try {
      await api.put('/auth/me', profile);
      toast.success('Profil zaktualizowany — odswiez strone (F5) zeby zobaczyc zmiany w sidebarze');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad zapisu profilu');
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (savingPw) return;
    if (!pw.old_password || !pw.new_password) {
      toast.error('Podaj oba hasla');
      return;
    }
    if (pw.new_password !== pw.confirm_password) {
      toast.error('Nowe hasla nie sa identyczne');
      return;
    }
    if (pw.new_password.length < 8) {
      toast.error('Nowe haslo min. 8 znakow');
      return;
    }
    setSavingPw(true);
    try {
      await api.post('/auth/change-password', pw);
      toast.success('Haslo zmienione — zapamietaj nowe!');
      setPw({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad zmiany hasla');
    } finally {
      setSavingPw(false);
    }
  };

  const strengthScore = (pwd) => {
    if (!pwd) return 0;
    let s = 0;
    if (pwd.length >= 8) s++;
    if (pwd.length >= 12) s++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
    if (/\d/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    return s;
  };
  const s = strengthScore(pw.new_password);
  const strengthLabel = ['bardzo słabe', 'słabe', 'średnie', 'dobre', 'silne', 'bardzo silne'][s];
  const strengthColor = ['bg-red-500', 'bg-red-400', 'bg-yellow-500', 'bg-yellow-400', 'bg-green-500', 'bg-green-600'][s];

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><User className="w-8 h-8" />Moj profil</h1>
        <p className="text-sm text-gray-500 mt-1">
          Zalogowany jako <span className="font-mono">{user?.email}</span>
          {user?.role && <Badge variant="outline" className="ml-2">{user.role}</Badge>}
        </p>
      </div>

      {/* Profil */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" />Dane osobowe</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={saveProfile} className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input value={user?.email || ''} readOnly disabled className="bg-muted/50" />
              <p className="text-xs text-muted-foreground mt-1">Email moze zmienic tylko admin.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Imie</Label>
                <Input value={profile.first_name} onChange={e => setProfile({...profile, first_name: e.target.value})} />
              </div>
              <div>
                <Label>Nazwisko</Label>
                <Input value={profile.last_name} onChange={e => setProfile({...profile, last_name: e.target.value})} />
              </div>
            </div>
            <Button type="submit" disabled={savingProfile}>
              {savingProfile && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Zapisz profil
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Zmiana hasla */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Lock className="w-4 h-4" />Zmiana hasla</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-3">
            <div>
              <Label>Aktualne haslo</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={pw.old_password}
                  onChange={e => setPw({...pw, old_password: e.target.value})}
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div>
              <Label>Nowe haslo (min. 8 znakow)</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={pw.new_password}
                  onChange={e => setPw({...pw, new_password: e.target.value})}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  title={showPw ? 'Ukryj' : 'Pokaz'}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {pw.new_password && (
                <div className="mt-2">
                  <div className="flex gap-1 h-1.5">
                    {[0,1,2,3,4].map(i => (
                      <div key={i} className={`flex-1 rounded ${i < s ? strengthColor : 'bg-gray-700'}`} />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Sila: {strengthLabel}</p>
                </div>
              )}
            </div>
            <div>
              <Label>Potwierdz nowe haslo</Label>
              <Input
                type={showPw ? 'text' : 'password'}
                value={pw.confirm_password}
                onChange={e => setPw({...pw, confirm_password: e.target.value})}
                autoComplete="new-password"
              />
              {pw.confirm_password && pw.new_password !== pw.confirm_password && (
                <p className="text-xs text-red-400 mt-1">Hasla sie nie zgadzaja</p>
              )}
            </div>
            <Separator />
            <Button type="submit" disabled={savingPw || !pw.old_password || !pw.new_password || pw.new_password !== pw.confirm_password}>
              {savingPw && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Zmien haslo
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        Konto utworzone: {user?.created_at ? new Date(user.created_at).toLocaleString('pl-PL') : '?'}
      </p>
    </div>
  );
}
